import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import type { Json } from "../integrations/supabase/database.types";
import { toPortalFailure, type PortalFailure } from "../lib/errors";
import {
  isFactsSchema,
  isInteger,
  isNullableString,
  isObject,
  isString,
  problemsFromDetails
} from "../lib/facts/shape";
import type { FactsProblem, FactsSchema, FactsValues } from "../lib/facts/types";
import { refuseInView, sourcedKey, useReadSource, viewValue } from "./readSource";

/**
 * Webbplatsens fakta, the client's own facts form, since build round 19 (2026-10-08), the third CMS
 * round of decision 0503: "Clients edit facts in the Portal, and the Portal can only start a build,
 * never write code."
 *
 * Four gateways from migration `20261008100001`, the client's own and nothing of Petrovic's. Any
 * active member reads; only an active authorised contact of a tenant open for writing saves and
 * publishes, and the read says which (`can_edit`), so the screen offers what the server would allow.
 * The server refuses the rest on its own. Publishing here is one act: the next version and a queued
 * build of the site, in one transaction, under round 17's caps.
 *
 * The same two rules as `adminFactsQueries.ts`. A read whose shape this screen cannot use is a failed
 * read and never an empty one: "no website has facts" over a malformed answer would hide the place,
 * and "inga versioner" over one would be a sentence somebody acts on. And every mutation returns its
 * invalidation, so TanStack Query counts it done only once the screen holds the fresh read
 * (`.claude/rules/a-mutation-is-done-when-its-read-is.md`): round 17's publish said "Publiceringen är
 * startad" beside the old counts because its invalidation was voided.
 *
 * Nothing here imports from the admin data modules: they are admin code, which stays out of the
 * client's entry chunk. The shape checks both sides need are in `app/lib/facts/shape.ts`.
 */

export const clientSiteFactsSitesKey = (tenantId: string) => ["portal", "site-facts-sites", tenantId] as const;

export const clientSiteFactsKey = (tenantId: string, serviceId: string) =>
  ["portal", "site-facts", tenantId, serviceId] as const;

/** One website the client has facts for: a stored schema and at least one published version. */
export interface ClientFactsSite {
  service_id: string;
  name: string;
  public_url: string | null;
  latest_version: number;
  published_at: string;
}

export type ClientFactsOrigin = "admin" | "client";

export interface ClientFactsPublished {
  version: number;
  published_at: string;
  origin: ClientFactsOrigin;
  /** "Petrovic" for Petrovic's, a colleague's name for the client's, null when it could not be read. */
  published_by: string | null;
  values: FactsValues;
}

/** The client's own draft, only for somebody who may edit and only when newer than the latest version. */
export interface ClientFactsDraft {
  values: FactsValues;
  saved_at: string;
  saved_by: string | null;
}

/**
 * `blocked_waiting_for_facts` since build round 20 (2026-10-08): the build was not sent, because the
 * website's facts fail its new schema and Petrovic has to fill them first. The screen says
 * "Väntar på fakta".
 */
export type ClientPublishStatus = "queued" | "dispatched" | "succeeded" | "failed" | "blocked_waiting_for_facts";

/** The latest site build the client started. Petrovic's own builds are not the client's to read. */
export interface ClientSitePublish {
  id: string;
  status: ClientPublishStatus;
  requested_at: string;
  completed_at: string | null;
}

export interface ClientFactsVersion {
  version: number;
  published_at: string;
  origin: ClientFactsOrigin;
  published_by: string | null;
  /** The labels of the fields that changed from the version before; null for the first version. */
  changed: string[] | null;
}

export interface ClientSiteFacts {
  service_id: string;
  name: string;
  public_url: string | null;
  /** An active authorised contact of a tenant open for writing. Everybody else reads. */
  can_edit: boolean;
  /** The active authorised contacts' names, sorted: who can change the facts. */
  editors: string[];
  schema: { sha: string; document: FactsSchema };
  published: ClientFactsPublished;
  draft: ClientFactsDraft | null;
  /** The draft revision a save or publish names; null when the reader may not edit. */
  revision: number | null;
  last_publish: ClientSitePublish | null;
  /** Newest first, at most fifty. */
  versions: ClientFactsVersion[];
  /**
   * The website's latest version fails its current schema, since build round 20 (2026-10-08), so a
   * build that failed is shown as waiting for facts and a publish is answered "blocked".
   */
  waiting_for_facts: boolean;
}

export interface ClientFactsTarget {
  tenantId: string;
  serviceId: string;
}

export interface ClientFactsWriteInput extends ClientFactsTarget {
  values: Record<string, unknown>;
  /** The stored schema's SHA the form was built from. */
  schemaSha: string;
  /** The draft revision the read gave when the form was built. */
  revision: number;
}

export interface ClientFactsSaved {
  revision: number;
  saved_at: string;
  values: FactsValues;
}

/**
 * What "Publicera på webbplatsen" answered. `blocked` is not a refusal: the site has no repository,
 * or its schema could not be read the last time, so the gateway alerted Petrovic and wrote nothing
 * else. It is an answer rather than an error because an error would have rolled the alert back.
 * Since build round 20 (2026-10-08) `reason: "waiting_for_facts"` is a third: the site's schema
 * changed and Petrovic's alert about it is already open.
 */
export type ClientFactsPublishResult =
  | { outcome: "queued"; version: number; published_at: string; publish: ClientSitePublish }
  | { outcome: "blocked"; reason: string };

/**
 * A refusal from the save or publish gateway. `PORTAL_SITE_FACTS_INVALID` carries the values'
 * problems in the error's detail and `PORTAL_SITE_FACTS_LOCKED` the fields the client may not change,
 * so the form can mark each field the database named.
 */
export interface ClientFactsFailure extends PortalFailure {
  problems: FactsProblem[];
}

// ---------------------------------------------------------------------------------------------------
// Shapes

const isOrigin = (value: unknown): value is ClientFactsOrigin => value === "admin" || value === "client";

const isStatus = (value: unknown): value is ClientPublishStatus =>
  value === "queued" ||
  value === "dispatched" ||
  value === "succeeded" ||
  value === "failed" ||
  value === "blocked_waiting_for_facts";

function isSite(value: unknown): value is ClientFactsSite {
  return (
    isObject(value) &&
    isString(value.service_id) &&
    isString(value.name) &&
    isNullableString(value.public_url) &&
    isInteger(value.latest_version) &&
    isString(value.published_at)
  );
}

function isPublish(value: unknown): value is ClientSitePublish {
  return (
    isObject(value) &&
    isString(value.id) &&
    isStatus(value.status) &&
    isString(value.requested_at) &&
    isNullableString(value.completed_at)
  );
}

function isPublished(value: unknown): value is ClientFactsPublished {
  return (
    isObject(value) &&
    isInteger(value.version) &&
    isString(value.published_at) &&
    isOrigin(value.origin) &&
    isNullableString(value.published_by) &&
    isObject(value.values)
  );
}

function isDraft(value: unknown): value is ClientFactsDraft {
  return isObject(value) && isObject(value.values) && isString(value.saved_at) && isNullableString(value.saved_by);
}

function isVersion(value: unknown): value is ClientFactsVersion {
  return (
    isObject(value) &&
    isInteger(value.version) &&
    isString(value.published_at) &&
    isOrigin(value.origin) &&
    isNullableString(value.published_by) &&
    (value.changed === null || (Array.isArray(value.changed) && value.changed.every(isString)))
  );
}

export function isClientSiteFacts(value: unknown): value is ClientSiteFacts {
  return (
    isObject(value) &&
    isString(value.service_id) &&
    isString(value.name) &&
    isNullableString(value.public_url) &&
    typeof value.can_edit === "boolean" &&
    Array.isArray(value.editors) &&
    value.editors.every(isString) &&
    isObject(value.schema) &&
    isString(value.schema.sha) &&
    isFactsSchema(value.schema.document) &&
    isPublished(value.published) &&
    (value.draft === null || isDraft(value.draft)) &&
    // Somebody who may edit is always given the revision to save against; without it the form could
    // send nothing the server would take.
    (value.can_edit ? isInteger(value.revision) : value.revision === null || isInteger(value.revision)) &&
    (value.last_publish === null || isPublish(value.last_publish)) &&
    Array.isArray(value.versions) &&
    value.versions.every(isVersion) &&
    // Build round 20: always carried, so a read without it is a failed read and never "not waiting".
    typeof value.waiting_for_facts === "boolean"
  );
}

function toFactsFailure(error: unknown): ClientFactsFailure {
  const failure = toPortalFailure(error);
  const details = isObject(error) ? error.details : undefined;
  const named = failure.code === "PORTAL_SITE_FACTS_INVALID" || failure.code === "PORTAL_SITE_FACTS_LOCKED";
  return { ...failure, problems: named ? problemsFromDetails(details) : [] };
}

const unexpected = (): ClientFactsFailure => ({ ...toPortalFailure(new Error("PORTAL_UNEXPECTED")), problems: [] });

/** Whether the build the client started is still on its way, so the read is asked again. */
export function publishInProgress(publish: ClientSitePublish | null | undefined): boolean {
  return publish?.status === "queued" || publish?.status === "dispatched";
}

// ---------------------------------------------------------------------------------------------------
// Reads

/**
 * The client's websites with facts. Empty means Webbplatsens fakta is not offered at all, so the
 * shell reads this too, and shows the place only on a successful, non-empty answer.
 */
export function useClientSiteFactsSites(tenantId: string | null): UseQueryResult<ClientFactsSite[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, clientSiteFactsSitesKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const value = await viewValue<unknown>("admin_as_client_site_facts_sites", { p_tenant_id: source.tenantId });
        if (!Array.isArray(value) || !value.every(isSite)) {
          throw toPortalFailure(new Error("PORTAL_UNEXPECTED"));
        }
        return value;
      }
      const { data, error } = await getSupabaseBrowserClient().rpc("client_site_facts_sites", {
        p_tenant_id: tenantId as string
      });

      if (error) {
        throw toPortalFailure(error);
      }
      const value: unknown = data;
      if (!Array.isArray(value) || !value.every(isSite)) {
        throw toPortalFailure(new Error("PORTAL_UNEXPECTED"));
      }
      return value;
    }
  });
}

/**
 * One website's facts. While the build the client started is queued or on its way, this asks again
 * every ten seconds, so "Publiceras" turns into its outcome without a reload. The tick dispatches
 * within a minute and a site's build takes a few, so ten seconds is often enough to follow it and
 * seldom enough to cost nothing.
 */
export function useClientSiteFacts(tenantId: string, serviceId: string): UseQueryResult<ClientSiteFacts, PortalFailure> {
  const source = useReadSource();
  return useQuery<ClientSiteFacts, PortalFailure>({
    queryKey: sourcedKey(source, clientSiteFactsKey(tenantId, serviceId)),
    refetchInterval: (query) => (publishInProgress(query.state.data?.last_publish) ? 10_000 : false),
    queryFn: async () => {
      if (source.kind === "admin") {
        // The role decides `can_edit`, `revision` and the draft, so the view answers as that role would.
        let value: unknown;
        try {
          value = await viewValue<unknown>("admin_as_client_site_facts", {
            p_tenant_id: source.tenantId,
            p_service_id: serviceId,
            p_role: source.role
          });
        } catch (error) {
          throw toPortalFailure(error);
        }
        if (!isClientSiteFacts(value)) {
          throw toPortalFailure(new Error("PORTAL_UNEXPECTED"));
        }
        return value;
      }
      const { data, error } = await getSupabaseBrowserClient().rpc("client_site_facts", {
        p_tenant_id: tenantId,
        p_service_id: serviceId
      });

      if (error) {
        throw toPortalFailure(error);
      }
      const value: unknown = data;
      if (!isClientSiteFacts(value)) {
        throw toPortalFailure(new Error("PORTAL_UNEXPECTED"));
      }
      return value;
    }
  });
}

// ---------------------------------------------------------------------------------------------------
// Writes

export function useClientSaveSiteFactsDraft() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<ClientFactsSaved, ClientFactsFailure, ClientFactsWriteInput>({
    mutationFn: async (input) => {
      refuseInView(source);
      const { data, error } = await getSupabaseBrowserClient().rpc("client_save_site_facts_draft", {
        p_tenant_id: input.tenantId,
        p_service_id: input.serviceId,
        // The values are JSON by construction: the form builds them from strings, numbers and booleans.
        p_values: input.values as Json,
        p_schema_sha: input.schemaSha,
        p_revision: input.revision
      });

      if (error) {
        throw toFactsFailure(error);
      }
      const value: unknown = data;
      if (!isObject(value) || !isInteger(value.revision) || !isString(value.saved_at) || !isObject(value.values)) {
        throw unexpected();
      }
      return { revision: value.revision, saved_at: value.saved_at, values: value.values as FactsValues };
    },
    // Settled, and returned: a draft that moved underneath the form is a reason to read it again too,
    // and the press is done only when the screen shows the saved draft.
    onSettled: (_result, _error, input) =>
      queryClient.invalidateQueries({ queryKey: clientSiteFactsKey(input.tenantId, input.serviceId) })
  });
}

export function useClientPublishSiteFacts() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<ClientFactsPublishResult, ClientFactsFailure, ClientFactsWriteInput>({
    mutationFn: async (input) => {
      refuseInView(source);
      const { data, error } = await getSupabaseBrowserClient().rpc("client_publish_site_facts", {
        p_tenant_id: input.tenantId,
        p_service_id: input.serviceId,
        p_values: input.values as Json,
        p_schema_sha: input.schemaSha,
        p_revision: input.revision
      });

      if (error) {
        throw toFactsFailure(error);
      }
      const value: unknown = data;
      if (isObject(value) && value.outcome === "blocked" && isString(value.reason)) {
        return { outcome: "blocked", reason: value.reason };
      }
      if (
        isObject(value) &&
        value.outcome === "queued" &&
        isInteger(value.version) &&
        isString(value.published_at) &&
        isPublish(value.publish)
      ) {
        return { outcome: "queued", version: value.version, published_at: value.published_at, publish: value.publish };
      }
      throw unexpected();
    },
    // Both reads: the facts hold the new version, the history and the build's status, and the site
    // list holds the latest version's number. Returned, so "Publiceras" and the new version appear in
    // the same render as the end of the press, never before the read that shows them.
    onSettled: (_result, _error, input) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: clientSiteFactsKey(input.tenantId, input.serviceId) }),
        queryClient.invalidateQueries({ queryKey: clientSiteFactsSitesKey(input.tenantId) })
      ])
  });
}
